import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast, Toaster } from "sonner";
import { Plus, Mail, Download, Copy, Trash2, Pencil, X, Gift, Check } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import api from "../../lib/api";
import { safeHtml } from "../../lib/safeHtml";

const TYPE_LABELS = {
  supplies: "Art supplies",
  course: "Course fee",
  custom: "Custom",
};

// Quick-start presets seeded from the user's request — admin can edit
// any field before generating the voucher, or click "Custom" for a blank.
const PRESETS = [
  { label: "£20 off art supplies", value_gbp: 20, type: "supplies", message: "Use any time at the studio shop." },
  { label: "£30 off the course fee", value_gbp: 30, type: "course", message: "Knock £30 off a single session or the whole course." },
  { label: "Custom voucher", value_gbp: 0, type: "custom", message: "" },
];

export default function Vouchers() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // open form payload
  const [previewing, setPreviewing] = useState(null); // {id, html}

  const load = async () => {
    try { const { data } = await api.get("/admin/vouchers"); setRows(data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load vouchers"); }
  };
  useEffect(() => { load(); }, []);

  const openNew = (preset) => setEditing({
    id: null,
    label: preset.label,
    value_gbp: preset.value_gbp,
    type: preset.type,
    recipient_name: "",
    recipient_email: "",
    message: preset.message || "",
    expires_on: "",
  });

  const save = async () => {
    if (!editing) return;
    if (!editing.label.trim()) { toast.error("Give the voucher a label"); return; }
    if (Number(editing.value_gbp) <= 0) { toast.error("Voucher value must be positive"); return; }
    try {
      if (editing.id) {
        await api.patch(`/admin/vouchers/${editing.id}`, editing);
      } else {
        await api.post("/admin/vouchers", editing);
      }
      toast.success("Voucher saved ✓");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this voucher?")) return;
    await api.delete(`/admin/vouchers/${id}`);
    load();
  };

  const togglePaid = async (v) => {
    await api.patch(`/admin/vouchers/${v.id}`, { redeemed_at: v.redeemed_at ? null : new Date().toISOString() });
    load();
  };

  const preview = async (v) => {
    try {
      const { data } = await api.get(`/admin/vouchers/${v.id}/html`);
      setPreviewing({ id: v.id, code: v.code, html: data.html });
    } catch (e) { toast.error("Couldn't load preview"); }
  };

  const sendEmail = async (v, override) => {
    const to = override || prompt("Send to which email?", v.recipient_email || "");
    if (!to) return;
    try {
      await api.post(`/admin/vouchers/${v.id}/email`, { to });
      toast.success(`Voucher emailed to ${to} ✓`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
  };

  const copyCode = (code) => { navigator.clipboard.writeText(code); toast.success(`Copied ${code}`); };

  return (
    <div className="admin-section">
      <Toaster richColors position="top-right" />
      <header className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="font-display text-3xl text-[#5D2C8F] flex items-center gap-2"><Gift size={24} /> Gift vouchers</h1>
          <p className="text-sm text-[#6E6074] mt-1">Create a printable/email-able gift voucher in seconds. Pick a preset to start, then customise.</p>
        </div>
      </header>

      {/* Preset launchers */}
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        {PRESETS.map((p, idx) => (
          <button
            key={p.label}
            onClick={() => openNew(p)}
            className="admin-surface p-4 text-left hover:border-[#5D2C8F] transition-colors"
            data-testid={`voucher-preset-${idx}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Plus size={16} className="text-[#5D2C8F]" />
              <span className="text-sm font-medium text-[#5D2C8F]">{p.label}</span>
            </div>
            <div className="text-xs text-[#6E6074]">{p.value_gbp > 0 ? `£${p.value_gbp.toFixed(0)} · ${TYPE_LABELS[p.type]}` : "Blank — set everything yourself"}</div>
          </button>
        ))}
      </div>

      {/* Existing vouchers */}
      <div className="admin-surface p-5">
        <h2 className="font-display text-xl text-[#5D2C8F] mb-3">Your vouchers ({rows.length})</h2>
        {rows.length === 0 ? (
          <div className="text-sm text-[#6E6074] bg-[#FAF6EE] rounded-lg p-6 text-center">No vouchers yet — pick a preset above to make your first one.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="vouchers-table">
              <thead>
                <tr className="text-left text-[11px] text-[#6E6074] uppercase tracking-wide border-b border-[#E5DCE6]">
                  <th className="py-2 pr-3">Code</th>
                  <th className="py-2 pr-3">Label</th>
                  <th className="py-2 pr-3">Value</th>
                  <th className="py-2 pr-3">Recipient</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((v) => (
                  <tr key={v.id} className={`border-b border-[#F1ECE3] ${v.redeemed_at ? "opacity-60" : ""}`} data-testid={`voucher-row-${v.id}`}>
                    <td className="py-2 pr-3">
                      <button onClick={() => copyCode(v.code)} className="font-mono text-xs text-[#5D2C8F] inline-flex items-center gap-1 hover:underline" data-testid={`voucher-copy-${v.id}`}>
                        {v.code} <Copy size={11} className="opacity-60" />
                      </button>
                    </td>
                    <td className="py-2 pr-3 text-[#3A2A47]">{v.label}</td>
                    <td className="py-2 pr-3 text-[#5D2C8F] font-medium">£{(v.value_gbp || 0).toFixed(0)}</td>
                    <td className="py-2 pr-3 text-xs">
                      {v.recipient_name ? <div className="text-[#3A2A47]">{v.recipient_name}</div> : <span className="text-[#9A8DA6]">—</span>}
                      {v.recipient_email && <div className="text-[#6E6074]">{v.recipient_email}</div>}
                    </td>
                    <td className="py-2 pr-3">
                      <button onClick={() => togglePaid(v)} className="text-xs" data-testid={`voucher-redeemed-${v.id}`}>
                        {v.redeemed_at
                          ? <span className="text-[#348469] inline-flex items-center gap-1"><Check size={11} /> Redeemed</span>
                          : <span className="text-[#C48A2B]">Active</span>}
                      </button>
                    </td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">
                      <button onClick={() => preview(v)} className="text-xs text-[#5D2C8F] mr-2 inline-flex items-center gap-1" data-testid={`voucher-preview-${v.id}`}><Download size={12} /> Preview/Download</button>
                      <button onClick={() => sendEmail(v)} className="text-xs text-[#5D2C8F] mr-2 inline-flex items-center gap-1" data-testid={`voucher-send-${v.id}`}><Mail size={12} /> Email</button>
                      <button onClick={() => setEditing({ ...v, expires_on: v.expires_on || "" })} className="text-xs text-[#5D2C8F] mr-2 inline-flex items-center gap-1" data-testid={`voucher-edit-${v.id}`}><Pencil size={12} /> Edit</button>
                      <button onClick={() => remove(v.id)} className="text-xs text-[#A685BD]" data-testid={`voucher-delete-${v.id}`}><Trash2 size={12} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <VoucherForm value={editing} onChange={setEditing} onSave={save} onClose={() => setEditing(null)} />
      )}
      {previewing && (
        <VoucherPreview voucher={previewing} onClose={() => setPreviewing(null)} />
      )}
    </div>
  );
}

function VoucherForm({ value, onChange, onSave, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="admin-surface w-full max-w-2xl p-5" onClick={(e) => e.stopPropagation()} data-testid="voucher-form">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl text-[#5D2C8F]">{value.id ? "Edit voucher" : "New voucher"}</h2>
          <button onClick={onClose} className="text-[#A685BD]" aria-label="Close" data-testid="voucher-form-close"><X size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <Field label="Label (shown on voucher)">
            <input value={value.label} onChange={(e) => onChange({ ...value, label: e.target.value })} className="w-full bg-white border border-[#E5E0D8] rounded px-3 py-2" data-testid="voucher-label" />
          </Field>
          <Field label="Value (£)">
            <input type="number" min="0" step="0.01" value={value.value_gbp} onChange={(e) => onChange({ ...value, value_gbp: e.target.value })} className="w-full bg-white border border-[#E5E0D8] rounded px-3 py-2" data-testid="voucher-value" />
          </Field>
          <Field label="Type">
            <select value={value.type} onChange={(e) => onChange({ ...value, type: e.target.value })} className="w-full bg-white border border-[#E5E0D8] rounded px-3 py-2" data-testid="voucher-type">
              {Object.entries(TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          <Field label="Expires on (optional)">
            <input type="date" value={value.expires_on || ""} onChange={(e) => onChange({ ...value, expires_on: e.target.value })} className="w-full bg-white border border-[#E5E0D8] rounded px-3 py-2" data-testid="voucher-expires" />
          </Field>
          <Field label="Recipient name (optional)">
            <input value={value.recipient_name || ""} onChange={(e) => onChange({ ...value, recipient_name: e.target.value })} placeholder="Will print on the voucher" className="w-full bg-white border border-[#E5E0D8] rounded px-3 py-2" data-testid="voucher-recipient-name" />
          </Field>
          <Field label="Recipient email (optional)">
            <input type="email" value={value.recipient_email || ""} onChange={(e) => onChange({ ...value, recipient_email: e.target.value })} placeholder="So you can email it to them" className="w-full bg-white border border-[#E5E0D8] rounded px-3 py-2" data-testid="voucher-recipient-email" />
          </Field>
          <Field label="Personal message (optional)" full>
            <textarea rows={2} value={value.message || ""} onChange={(e) => onChange({ ...value, message: e.target.value })} placeholder="Happy birthday! Hope you treat yourself…" className="w-full bg-white border border-[#E5E0D8] rounded px-3 py-2" data-testid="voucher-message" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="text-sm text-[#A685BD] px-3 py-2" data-testid="voucher-cancel">Cancel</button>
          <button onClick={onSave} className="sac-btn-primary text-sm" data-testid="voucher-save">{value.id ? "Save changes" : "Create voucher"}</button>
        </div>
      </div>
    </div>
  );
}

function VoucherPreview({ voucher, onClose }) {
  const ref = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    if (!ref.current) return;
    setDownloading(true);
    try {
      // html2canvas → PNG → jsPDF. Single A5-ish landscape page so the
      // voucher prints cleanly on standard paper.
      const canvas = await html2canvas(ref.current, { scale: 2, backgroundColor: "#FAF6EE" });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a5" });
      const w = pdf.internal.pageSize.getWidth();
      const h = pdf.internal.pageSize.getHeight();
      // Center + maintain aspect
      const imgRatio = canvas.width / canvas.height;
      let drawW = w - 16; let drawH = drawW / imgRatio;
      if (drawH > h - 16) { drawH = h - 16; drawW = drawH * imgRatio; }
      pdf.addImage(img, "PNG", (w - drawW) / 2, (h - drawH) / 2, drawW, drawH);
      pdf.save(`voucher-${voucher.code}.pdf`);
      toast.success("Saved PDF ✓");
    } catch (e) {
      toast.error("Couldn't generate PDF");
    } finally { setDownloading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="voucher-preview">
        <div className="flex items-center justify-between p-4 border-b border-[#E5E0D8]">
          <h3 className="font-display text-lg text-[#5D2C8F]">Voucher preview — {voucher.code}</h3>
          <div className="flex items-center gap-2">
            <button onClick={downloadPdf} disabled={downloading} className="sac-btn-primary text-xs" data-testid="voucher-download-pdf"><Download size={12} /> {downloading ? "Generating…" : "Download PDF"}</button>
            <button onClick={onClose} className="text-[#A685BD]" aria-label="Close" data-testid="voucher-preview-close"><X size={18} /></button>
          </div>
        </div>
        <div className="p-6">
          <div ref={ref} dangerouslySetInnerHTML={safeHtml(voucher.html)} />
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-xs text-[#5D2C8F] block mb-1">{label}</span>
      {children}
    </label>
  );
}
